import { useState } from "react";
import { Behavior } from "@/types/behavior.types";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Trash2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BehaviorCardProps {
  behavior: Behavior;
  onEdit: () => void;
  onDelete: (id: string) => void;
  onUpdate?: (updatedBehavior: Behavior) => void;
}

const BehaviorCard = ({ behavior, onEdit, onDelete, onUpdate }: BehaviorCardProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();

  const handleToggleActive = async (checked: boolean) => {
    setIsToggling(true);
    try {
      const { data, error } = await supabase
        .from("behaviors")
        .update({ is_active: checked })
        .eq("id", behavior.id)
        .select("*")
        .single();
        
      if (error) throw error;
      
      toast({
        title: `Behavior ${checked ? 'activated' : 'deactivated'}`,
        description: `The behavior has been ${checked ? 'activated' : 'deactivated'}.`,
      });
      
      if (onUpdate) {
        onUpdate(data as Behavior);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error updating behavior status",
        description: error.message || "An unexpected error occurred",
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("behaviors")
        .delete()
        .eq("id", behavior.id);
        
      if (error) throw error;
      
      toast({
        title: "Behavior deleted",
        description: "The behavior has been successfully deleted.",
      });
      
      onDelete(behavior.id);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting behavior",
        description: error.message || "An unexpected error occurred",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <>
      <Card 
        className="shadow-sm hover:bg-white transition-all hover:shadow-md cursor-pointer w-full max-w-full"
        onClick={onEdit}
      >
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex-1 min-w-0 mr-2 overflow-hidden">
            <div className="mb-1">
              <h4 className="font-medium text-ellipsis overflow-hidden whitespace-nowrap block">
                {behavior.title}
              </h4>
            </div>
            <div className="mb-1">
              <p className="text-sm text-muted-foreground text-ellipsis overflow-hidden whitespace-nowrap block">
                {behavior.description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Switch
                      checked={behavior.is_active || false}
                      onCheckedChange={handleToggleActive}
                      disabled={isToggling}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {behavior.is_active 
                      ? "Active - Your agent will use this behavior"
                      : "Inactive - Your agent will not use this behavior"
                    }
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteDialog(true);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this behavior.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BehaviorCard;
